import React from 'react';

import { Link, Typography } from '@sunwisesoftware/sunwise-ui';
import { isEmpty, isNull, orderBy } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { useBreakpoint } from 'common/hooks';
import { formatDate, formatDistanceDate, parseDate } from 'common/utils/dates';

import ActionsFormatter from './ActionsFormatter';

const ProductColumns = ({
    filterData,
    handleClickBulkItems,
    hiddenColumns,
    orders,
    rowModesModel,
    selectedRowId,
    setRowModesModel,
    setSelectedRowId,
}) => {
    const { t } = useTranslation();
    const breakpoint = useBreakpoint();

    const nameColumn = [
        {
            align: 'left',
            display: 'flex',
            editable: true,
            field: 'name',
            flex: 1,
            headerName: t('Name'),
            hide: hiddenColumns.includes('name'),
            minWidth: 180,
            order: orders?.name,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Link
                        component={RouterLink}
                        to={`/contacts/${row.id}`}
                        sx={{ color: '#000' }}
                    >
                        <Typography fontWeight="bold" variant="caption">
                            {row.name}
                        </Typography>
                    </Link>
                );
            },
        },
    ];

    if (['xs', 'sm'].includes(breakpoint)) {
        return nameColumn;
    }

    const columns = [
        ...nameColumn,
        {
            align: 'left',
            display: 'flex',
            editable: true,
            field: 'telephone',
            flex: 1,
            headerName: t('Telephone'),
            hide: hiddenColumns.includes('telephone'),
            minWidth: 180,
            order: orders?.telephone,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography fontWeight="bold" variant="caption">
                        {row?.telephone}
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            editable: true,
            field: 'email',
            flex: 1,
            headerName: t('Mail'),
            hide: hiddenColumns.includes('email'),
            minWidth: 250,
            order: orders?.email,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography fontWeight="bold" variant="caption">
                        {row?.email}
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            editable: false,
            field: 'company_name',
            flex: 1,
            headerName: t('Company'),
            hide: hiddenColumns.includes('company_name'),
            minWidth: 250,
            order: orders?.company_name,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography fontWeight="bold" variant="caption">
                        {row?.company_name || '- - -'}
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'installer',
            flex: 1,
            headerName: t('User'),
            hide:
                !isEmpty(rowModesModel) || hiddenColumns.includes('installer'),
            minWidth: 180,
            order: orders?.installer,
            renderCell: (params) => {
                const { row } = params;
                if (isNull(row?.user)) return t('No installer assigned');
                return `${row?.user?.first_name} ${row?.user?.last_name}`;
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'projects',
            flex: 1,
            headerName: t('Project', { count: 2 }),
            hide: !isEmpty(rowModesModel) || hiddenColumns.includes('projects'),
            minWidth: 180,
            order: orders?.projects,
            valueGetter: (_, row) => row?.projects || '- - -',
        },
        {
            align: 'left',
            display: 'flex',
            field: 'created_at',
            flex: 1,
            headerName: t('Oldest'),
            hide:
                !isEmpty(rowModesModel) || hiddenColumns.includes('created_at'),
            minWidth: 180,
            order: orders?.created_at,
            renderCell: (params) => {
                const { row } = params;
                if (row?.created_at) {
                    const createdAt = formatDate(
                        parseDate(row?.created_at, 'dd/MM/yyyy HH:mm:ss'),
                        'MM/dd/yyyy HH:mm:ss',
                    );
                    return formatDistanceDate(
                        parseDate(createdAt, 'MM/dd/yyyy HH:mm:ss'),
                        new Date(),
                        { addSuffix: true },
                    );
                }
                return null;
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: '',
            flex: 1,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <ActionsFormatter
                        filterData={filterData}
                        handleClickBulkItems={handleClickBulkItems}
                        row={row}
                        rowModesModel={rowModesModel}
                        selectedRowId={selectedRowId}
                        setRowModesModel={setRowModesModel}
                        setSelectedRowId={setSelectedRowId}
                    />
                );
            },
            sortable: false,
        },
    ];

    return orderBy(columns, ['order'], ['asc']);
};

ProductColumns.propTypes = {
    filterData: PropTypes.object,
    handleClickBulkItems: PropTypes.func,
    hiddenColumns: PropTypes.array,
    orders: PropTypes.object,
    rowModesModel: PropTypes.object,
    selectedRowId: PropTypes.func,
    setRowModesModel: PropTypes.func,
    setSelectedRowId: PropTypes.func,
};

export default ProductColumns;
